import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserShield } from "react-icons/fa";
import { FiHome, FiArrowLeft, FiLogOut } from "react-icons/fi";
import {
  filterRoutesByPermissions,
  getFirstAllowedPath,
  getStoredUser,
  clearStoredUser,
} from "../../../Utils/permissionAccess";
import routes from "../../../Utils/routes";

const Unauthorized = () => {
  const navigate = useNavigate();

  const user = getStoredUser();
  const allowedRoutes = filterRoutesByPermissions(routes, user);
  const firstAllowedPath = getFirstAllowedPath(allowedRoutes) || "/profile";

  // Check if user has no assigned permissions at all
  const hasZeroPermissions = !user?.permissions || user.permissions.length === 0;

  useEffect(() => {
    // Only automatically redirect if they have actual module permissions
    if (!hasZeroPermissions && firstAllowedPath && firstAllowedPath !== "/unauthorized") {
      navigate(firstAllowedPath, { replace: true });
    }
  }, [firstAllowedPath, navigate, hasZeroPermissions]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate(firstAllowedPath);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    clearStoredUser();
    navigate("/login");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center transition-all duration-300 hover:shadow-2xl">
        {/* Animated Icon Container */}
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-50 text-red-500 mb-6 animate-pulse">
          <FaUserShield className="w-12 h-12" />
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
          Access Denied
        </h1>
        
        {/* Subtitle */}
        <h2 className="text-lg font-semibold text-red-600 mb-4">
          Error 403: Forbidden
        </h2>

        {/* Description */}
        <p className="text-gray-500 mb-8 leading-relaxed">
          {hasZeroPermissions
            ? "Your account currently has no assigned permissions. Please contact your system administrator to assign roles, or log out."
            : "You do not have the required permissions to view this resource. If you believe this is an error, please contact your system administrator."}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {hasZeroPermissions ? (
            <>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-600 text-white hover:bg-red-700 font-medium transition-all duration-200 shadow-md shadow-red-200 hover:shadow-lg"
              >
                <FiLogOut className="w-5 h-5" />
                Logout
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-200"
              >
                View Profile
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleGoBack}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-200"
              >
                <FiArrowLeft className="w-5 h-5" />
                Go Back
              </button>
              
              <button
                onClick={handleGoHome}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-medium transition-all duration-200 shadow-md shadow-blue-200 hover:shadow-lg"
              >
                <FiHome className="w-5 h-5" />
                Go to Home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
